import { GetUsedCarsResponse, SearchFilters } from '@cinch-nx/data-search'
import axios from 'axios'
import { useRouter } from 'next/router'
import useSWR from 'swr'
import { getUsedCarsApiRequestUrlForUrlPath } from '../../utils'
import { searchFilterKeys } from '../../constants/filter-constants'
import { useSearchStore } from '../use-search-store/use-search-store'
import { useShallow } from 'zustand/react/shallow'
import { useEffect, useMemo } from 'react'
import isEqual from 'lodash/isEqual'
import { keepPreviousDataWhileLoadingNewDataMiddleware } from '../../swr-middleware/keep-previous-data-while-loading-new-data-middleware'
import { useSsrExperiments } from '@cinch-nx/adobe-server-side'
import { Env, readFromEnv } from '@cinch-nx/environments'

const httpClient = axios.create()

const fetcher = async (
  url: string,
  shouldUseDataDrivenSortOrderAsDefault: boolean,
): Promise<GetUsedCarsResponse> =>
  httpClient
    .get(url, {
      headers: {
        'x-search-api-consumer': 'used-cars-page-hook',
        'x-search-experiment-data-driven-sort':
          shouldUseDataDrivenSortOrderAsDefault ? 'true' : 'false',
      },
    })
    .then((res) => res.data)

const getUrlForPageNumber = (page: number, asPath: string) => {
  const [urlPart, searchParamsPart] = asPath.split('?')

  const searchParams = new URLSearchParams(searchParamsPart)

  if (page === 1) {
    searchParams.delete('pageNumber')
  } else {
    searchParams.set('pageNumber', `${page}`)
  }

  if ([...searchParams.values()].length > 0) {
    // sort the query params to maintain a stable order
    searchParams.sort()
    return `${urlPart}?${searchParams}`
  }
  return urlPart
}

const getDataFromResponse = (response: GetUsedCarsResponse | undefined) => {
  const searchResults = response
    ? {
        pageNumber: response.pageNumber,
        pageSize: response.pageSize,
        searchResultsCount: response.searchResultsCount,
        vehicleListings: response.vehicleListings,
        facets: response.facets,
      }
    : undefined

  return searchResults
}

/**
 * Extracts the value of the 'url' query parameter from a given API URL.
 *
 * @param apiUrl - The API URL from which to extract the 'url' query parameter.
 * @returns The value of the 'url' query parameter if it exists, or `null` if it does not exist
 *          or if the provided string is not a valid URL.
 */
const extractSearchUrl = (apiUrl: string): string | null => {
  try {
    const urlObj = new URL(apiUrl)
    return urlObj.searchParams.get('url') ?? null
  } catch {
    return null
  }
}

// store the previous response so we can use it while waiting for new data,
// and so we can check the data has actually changed
let previousData: GetUsedCarsResponse | undefined

export function useUsedCarsData() {
  // NOTE: according the next.js useRouter docs, we shouldn't use `asPath` without checking `isReady`, and that can only be checked in a `useEffect`
  // Not doing so can cause issues when using SSR, with mismatches in the value between SSR and CSR
  // https://nextjs.org/docs/13/pages/api-reference/functions/use-router#router-object
  // But... looking at how it's used elsewhere in cinch-nx, no one is actually doing that (including us in the old useSearchFilters hook),
  // and no one has reported any issues, so we're going to use it as-is, without the `isReady` check.
  const { asPath } = useRouter()
  const requestUrl = getUsedCarsApiRequestUrlForUrlPath(asPath)

  const searchParamMutators = useSearchStore(
    useShallow((state) => ({
      updateFilter: state.updateFilter,
      updateFilters: state.updateFilters,
      clearFilterValue: state.clearFilterValue,
      clearAllFilters: state.clearAllFilters,
      setPageNumber: state.setPageNumber,
      setSortingCriteria: state.setSortingCriteria,
      setUseMonthly: state.setUseMonthly,
    })),
  )

  const searchParams = useSearchStore(
    useShallow((state) => ({
      bodyType: state.bodyType,
      colour: state.colour,
      condition: state.condition,
      seats: state.seats,
      doors: state.doors,
      fromEngineSize: state.fromEngineSize,
      toEngineSize: state.toEngineSize,
      fuelType: state.fuelType,
      make: state.make,
      model: state.model,
      trim: state.trim,
      mileage: state.mileage,
      fromPrice: state.fromPrice,
      toPrice: state.toPrice,
      gearbox: state.gearbox,
      fromYear: state.fromYear,
      toYear: state.toYear,
      driveType: state.driveType,
      store: state.store,
      tags: state.tags,
      features: state.features,
      financeType: state.financeType,
      sortingCriteria: state.sortingCriteria,
      pageNumber: state.pageNumber,
      pageSize: state.pageSize,
      useMonthly: state.useMonthly,
      vehicleYear: state.vehicleYear,
      rangeInMiles: state.rangeInMiles,
    })),
  )

  const updateSearchParamsStateFromResponse = useSearchStore(
    useShallow((state) => state.updateSearchParamsStateFromResponse),
  )

  const { getExperienceIdForExperiment } = useSsrExperiments()

  const isDataDrivenSortExperimentEnabled =
    getExperienceIdForExperiment(
      readFromEnv(Env.SearchDataDrivenSortExperimentId),
    ) === 1

  const isDataDrivenSortOrderFlagEnabled =
    readFromEnv(Env.SearchDataDrivenSortOrder) === 'true'

  const shouldUseDataDrivenSortOrderAsDefault =
    isDataDrivenSortOrderFlagEnabled && isDataDrivenSortExperimentEnabled

  const {
    isValidating,
    isLoading,
    data,
    error: usedCarsApiError,
  } = useSWR(
    requestUrl,
    // SWR docs say not to do this (https://swr.vercel.app/docs/arguments#multiple-arguments), but it should be ok here, since the experiment state will
    // not change within a session, and this is only temporary until the experiment ends
    (url) => fetcher(url, shouldUseDataDrivenSortOrderAsDefault),
    {
      // the `keepPreviousData` property feels like it should do the same as this, but seems to have some subtle differences
      // as it causes values to not be updated correctly in some cases
      use: [keepPreviousDataWhileLoadingNewDataMiddleware],
    },
  )

  // when we get data back from the server, update the store to keep it in sync...
  // but only when the state has actually changed
  useEffect(() => {
    if (data && !isEqual(data, previousData)) {
      previousData = data
      updateSearchParamsStateFromResponse(
        data.parsedParamsFromUrl,
        data.pathSegmentsFromUrl,
      )
    }
  }, [data, updateSearchParamsStateFromResponse])

  const totalFilterCount = useMemo(
    () =>
      Object.entries(searchParams).reduce(
        (currentCount, [paramKey, paramValue]) => {
          // only increment the count if it's a filter param, things like pageSize/useMonthly/sortingCriteria, or financeType=any should not be included in this count
          if (
            !(searchFilterKeys as ReadonlyArray<string>).includes(paramKey) ||
            (paramKey === 'financeType' &&
              (paramValue as SearchFilters['financeType']).includes('any'))
          ) {
            return currentCount
          }
          if (Array.isArray(paramValue)) {
            return currentCount + paramValue.length
          }
          return currentCount + 1
        },
        0,
      ),
    [searchParams],
  )

  const pathSegmentFilters: Partial<SearchFilters> = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(data?.pathSegmentsFromUrl ?? {}).map(([key, value]) => [
          key,
          value.split(','),
        ]),
      ),
    [data],
  )

  const hasMatchingVehicleRecentlyBeenInStock =
    data?.hasMatchingVehicleRecentlyBeenInStock

  // get the relevant parts from either the current data or the previousData (if we're currently fetching new data)
  const searchResults = getDataFromResponse(data)

  // Extract the actual search URL
  const searchUrl = extractSearchUrl(requestUrl)

  return {
    // search results/API request props:
    isValidating,
    isLoading,
    usedCarsApiError,
    searchResults,
    hasMatchingVehicleRecentlyBeenInStock,
    // search filters/params props:
    searchParams,
    totalFilterCount,
    pathSegmentFilters,
    searchUrl,
    // search filters/params setters:
    ...searchParamMutators,
    // pagination:
    getUrlForPageNumber: (pageNumber: number) =>
      getUrlForPageNumber(pageNumber, asPath),
  }
}
